<template>
  <AppContainer class="my-10 md:my-20">
    <div class="row">
      <div class="col-lg-1"></div>

      <div class="col-lg-8">
        <h1 class="mb-2 md:mb-5">{{ $t('page.faqs.title') }}</h1>

        <p class="lead mb-6 md:mb-16">
          {{ $t('page.faqs.description') }}
        </p>

        <AppListLinks class="!mb-8 md:!mb-12" :links="links" />

        <p class="lead">{{ $t('page.faqs.extra.description') }}</p>

        <div class="ui-accordion__item">
          <Disclosure v-slot="{ open }" :default-open="false" >
            <DisclosureButton ref="disclosureButton" class="ui-accordion__button">
              <div class="app-email-link">
                <span class="app-list-links__icon"><IconMail/></span>
                <span class="app-list-links__title h4">{{ $t('page.faqs.extra.contact.title') }}</span>
              </div>
              <UiButtonIconRounded as="span">
                <IconMinus v-if="open" />
                <IconPlus v-else />
              </UiButtonIconRounded>
            </DisclosureButton>
            <UiTransitionExpand>
              <DisclosurePanel class="py-2 px-1">
                <NuxtLink to="#brevoConversationsExpanded" :external="true" style="text-decoration: none;">
                  <div class="flex gap-2 contact-container">
                    <div class="w-5 pb-1 mr-2">
                      <IconChat />
                    </div>
                    <div class="grow">
                      {{ $t('page.faqs.extra.contact.contact_chat') }}
                    </div>
                    <div class="copy-to-buffer-text">{{ $t('page.faqs.extra.contact.open_chat') }}</div>
                  </div>
                </NuxtLink>
                <div v-if="site.data.emailAddress" class="flex gap-2 contact-container cursor-pointer" @click="copyEmailAddressToBuffer">
                  <div class="w-5 pb-1 mr-2">
                    <IconMail />
                  </div>
                  <div class="grow">
                    {{ site.data.emailAddress }}
                  </div>
                  <div class="copy-to-buffer-text">{{ $t('page.faqs.extra.contact.copy_email') }}</div>
                </div>
                <div v-if="site.data.phoneNumber" class="flex gap-2 contact-container">
                  <div class="w-5 pb-1 mr-2">
                    <IconUser />
                  </div>
                  <div class="grow">
                    {{ site.data.phoneNumber }}
                  </div>
                  <div class="copy-to-buffer-text">{{ $t('page.faqs.extra.contact.call_us') }}</div>
                </div>
              </DisclosurePanel>
            </UiTransitionExpand>
          </Disclosure>
        </div>
      </div>
    </div>
  </AppContainer>
</template>

<script lang="ts" setup>
import { fetchApi, queryKey } from '~/queries'
import IconMail from '~/components/IconMail.vue'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import UiTransitionExpand from 'ui/src/components/UiTransitionExpand.vue'
import IconPlus from 'ui/src/components/icons/IconPlus.vue'
import UiButtonIconRounded from 'ui/src/components/UiButtonIconRounded.vue'
import IconMinus from 'ui/src/components/icons/IconMinus.vue'
import IconChat from 'ui/src/components/icons/IconChat.vue'

const site = useSiteStore()
const { $i18n, $toast } = useNuxtApp()
const { t } = $i18n

const { data } = await useAsyncData(queryKey('faqs'), async () => {
  return await fetchApi(`/faqs`)
})

const disclosureButton = ref(null);

const copyEmailAddressToBuffer = () => {
  if (copyToClipboard(site.data.emailAddress)) {
    $toast.success(t('feedback.copy.success'))
  }
}

const links = computed(() =>
  data?.value?.items
    ?.sort((a: any, b: any) => a.data.position - b.data.position)
    .map((item: any) => ({
      title: item.translation.data.name,
      href: `/faqs/${item.translation.data.slug}`
    }))
)

useSeoMeta({
  title: 'FAQs'
})
</script>

<style lang="scss" scoped>
@import '@boats/ui/styles/config';

.contact-container:hover .copy-to-buffer-text {
  opacity: 0.8;
}

@include media-breakpoint-down(lg) {
  .copy-to-buffer-text {
    display: none;
  }
}

.app-email-link {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 3.25rem;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  transition-property: color;
  transition-duration: 0.25s;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

.copy-to-buffer-text {
  transition: opacity 0.2s linear;
  opacity: 0;
  padding-top: 0.75rem;
  padding-bottom: 1.5rem;
  font-size: 1rem;
  color: #5a5a5a;
}
</style>
